
import { MOCK_STORIES, Story } from '@/utils/storiesData';
import { contentfulClient } from './contentfulClient';
import { transformStory } from './contentfulTransformers';
import { findAppropriateContentType } from './contentTypeUtils';

export async function getStories(): Promise<Story[]> {
  try {
    console.log('Fetching stories from Contentful...');
    
    // Find appropriate content type
    const contentTypeId = await findAppropriateContentType('story');
    console.log(`Using content type ID: "${contentTypeId}"`);
    
    const response = await contentfulClient.getEntries({
      content_type: contentTypeId,
      include: 2, // Include linked entries up to 2 levels deep
    });
    
    console.log(`Successfully fetched ${response.items.length} stories from Contentful`);
    
    if (response.items.length === 0) {
      console.log('No stories found in Contentful. Check your content type and content entries.');
      return MOCK_STORIES;
    }
    
    // Debug the response structure - use a safer approach
    console.log('First entry ID:', response.items[0]?.sys?.id);
    
    try {
      return response.items.map(transformStory);
    } catch (transformError) {
      console.error('Error transforming Contentful data:', transformError);
      if (transformError instanceof Error) {
        console.error(`Transform error details: ${transformError.message}`);
        console.error(`Transform error stack: ${transformError.stack}`);
      }
      return MOCK_STORIES;
    }
  } catch (error) {
    console.error('Error fetching stories from Contentful:', error);
    
    // More detailed error logging
    if (error instanceof Error) {
      console.error(`Error message: ${error.message}`);
      console.error(`Error stack: ${error.stack}`);
      
      // Check for common errors
      if (error.message.includes('The access token you sent could not be found')) {
        console.error('Authentication error: Invalid access token');
      } else if (error.message.includes('service is temporarily unavailable')) {
        console.error('Contentful service unavailable');
      } else if (error.message.includes('content type does not exist')) {
        console.error('Content type does not exist in your Contentful space');
      }
    }
    
    // Return mock data as fallback in case of error
    console.log('Falling back to mock data');
    return MOCK_STORIES;
  }
}

export async function getStoryBySlug(slug: string): Promise<Story | null> {
  try {
    const contentTypeId = await findAppropriateContentType('story');
    
    console.log(`Fetching story with slug: ${slug} using content type: ${contentTypeId}`);
    const response = await contentfulClient.getEntries({
      content_type: contentTypeId,
      'fields.slug': slug,
      include: 2, // Include linked entries up to 2 levels deep
    });
    
    if (response.items.length > 0) {
      console.log('Story found in Contentful');
      return transformStory(response.items[0]);
    }
    
    console.log('Story not found in Contentful');
    // Try to find story in mock data
    const mockStory = MOCK_STORIES.find(s => s.slug === slug);
    return mockStory || null;
  } catch (error) {
    console.error(`Error fetching story with slug ${slug}:`, error);
    // Try to find story in mock data
    const mockStory = MOCK_STORIES.find(s => s.slug === slug);
    return mockStory || null;
  }
}

export async function getStoriesByCategory(category: string): Promise<Story[]> {
  try {
    const contentTypeId = await findAppropriateContentType('story');
    
    console.log(`Fetching stories for category: ${category} using content type: ${contentTypeId}`);
    const response = await contentfulClient.getEntries({
      content_type: contentTypeId,
      'fields.category': category,
      include: 2,
    });
    
    console.log(`Found ${response.items.length} stories in category '${category}'`);
    return response.items.map(transformStory);
  } catch (error) {
    console.error(`Error fetching stories for category ${category}:`, error);
    // Filter mock data by category as fallback
    const mockStoriesByCategory = MOCK_STORIES.filter(
      story => story.category.toLowerCase() === category.toLowerCase()
    );
    console.log(`Falling back to ${mockStoriesByCategory.length} mock stories`);
    return mockStoriesByCategory;
  }
}

export async function getFeaturedStories(): Promise<Story[]> {
  try {
    const contentTypeId = await findAppropriateContentType('story');
    
    console.log(`Fetching featured stories using content type: ${contentTypeId}`);
    const response = await contentfulClient.getEntries({
      content_type: contentTypeId,
      'fields.featured': true,
      include: 2,
    });
    
    console.log(`Found ${response.items.length} featured stories`);
    return response.items.map(transformStory);
  } catch (error) {
    console.error('Error fetching featured stories:', error);
    // Filter mock data for featured stories as fallback
    const mockFeaturedStories = MOCK_STORIES.filter(story => story.featured);
    console.log(`Falling back to ${mockFeaturedStories.length} mock featured stories`);
    return mockFeaturedStories;
  }
}
